"use client";

import React from 'react';
import { motion } from 'framer-motion';
import * as LucideIcons from 'lucide-react';
import { cn } from '@/lib/utils';

// Import high-fidelity animated icons
import { AccessibilityIcon } from './icons/AccessibilityIcon';
import { ShieldCheckIcon } from './icons/ShieldCheckIcon';
import { SparklesIcon } from './icons/SparklesIcon';
import { SearchIcon } from './icons/SearchIcon';
import { ZapIcon } from './icons/ZapIcon';
import { HomeIcon } from './icons/HomeIcon';
import { HeartIcon } from './icons/HeartIcon';
import { UsersIcon } from './icons/UsersIcon';
import { StarIcon } from './icons/StarIcon';
import { AwardIcon } from './icons/AwardIcon';

export type AnimationType = 'bounce' | 'pulse' | 'spin' | 'scale' | 'none';

interface AnimatedIconProps {
  name: string;
  size?: number;
  color?: string;
  className?: string;
  animation?: AnimationType;
  trigger?: 'hover' | 'always' | 'none';
}

const AnimatedIcon = ({ 
  name, 
  size = 24, 
  color = 'currentColor', 
  className,
  animation = 'none',
  trigger = 'always'
}: AnimatedIconProps) => {
  const iconNameLower = name.toLowerCase();
  const iconRef = React.useRef<any>(null);

  React.useEffect(() => {
    if (trigger === 'always') {
      iconRef.current?.startAnimation?.();
    } else if (trigger === 'none') {
      iconRef.current?.stopAnimation?.();
    }
  }, [trigger]);

  const handleMouseEnter = () => {
    if (trigger === 'hover' && iconRef.current?.startAnimation) {
      iconRef.current.startAnimation();
    }
  };

  const handleMouseLeave = () => {
    if (trigger === 'hover' && iconRef.current?.stopAnimation) {
      iconRef.current.stopAnimation();
    }
  };

  const commonProps = {
    size,
    color,
    className,
    ref: iconRef,
  };

  // 1. Check for high-fidelity animated versions first
  if (iconNameLower === 'accessibility') {
    return (
      <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <AccessibilityIcon {...commonProps} />
      </div>
    );
  }
  if (iconNameLower === 'shieldcheck' || iconNameLower === 'shield-check') {
    return (
      <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <ShieldCheckIcon {...commonProps} />
      </div>
    );
  }
  if (iconNameLower === 'sparkles') {
    return (
      <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <SparklesIcon {...commonProps} />
      </div>
    );
  }
  if (iconNameLower === 'search') {
    return (
      <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <SearchIcon {...commonProps} />
      </div>
    );
  }
  if (iconNameLower === 'zap') {
    return (
      <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <ZapIcon {...commonProps} />
      </div>
    );
  }
  if (iconNameLower === 'home') {
    return (
      <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <HomeIcon {...commonProps} />
      </div>
    );
  }
  if (iconNameLower === 'heart' || iconNameLower === 'hearthandshake') {
    return (
      <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <HeartIcon {...commonProps} />
      </div>
    );
  }
  if (iconNameLower === 'users') {
    return (
      <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <UsersIcon {...commonProps} />
      </div>
    );
  }
  if (iconNameLower === 'star') {
    return (
      <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <StarIcon {...commonProps} />
      </div>
    );
  }
  if (iconNameLower === 'award') {
    return (
      <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <AwardIcon {...commonProps} />
      </div>
    );
  }

  // 2. Fallback to standard Lucide icons with basic Framer Motion wrappers
  const LucideIcon = (LucideIcons as any)[name];

  if (!LucideIcon) {
    console.warn(`Icon "${name}" not found in lucide-react`);
    return null;
  }

  const variants = {
    bounce: {
      y: [0, -4, 0],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut"
      }
    },
    pulse: {
      scale: [1, 1.1, 1],
      opacity: [1, 0.8, 1],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut"
      }
    },
    spin: {
      rotate: 360,
      transition: {
        duration: 8,
        repeat: Infinity,
        ease: "linear"
      }
    },
    scale: {
      scale: 1,
      transition: { duration: 0.2 }
    },
    none: {}
  };

  const hoverVariants = {
    scale: { scale: 1.2 },
  };

  return (
    <motion.div
      className={cn("inline-flex items-center justify-center", className)}
      animate={(trigger === 'always' || trigger === 'hover') ? variants[animation as keyof typeof variants] : variants.none}
      whileHover={trigger === 'hover' ? hoverVariants.scale : undefined}
    >
      <LucideIcon size={size} color={color} aria-hidden="true" />
    </motion.div>
  );
};

export default AnimatedIcon;
